import EditIncomeDialog from './EditIncomeDialog';
import PaymentDetailCard from './PaymentDetailCard';
import EntityPartyLinks from '../../../../shared/components/EntityPartyLinks';

import { invoiceFormSchema } from '../../config/accountingForm.schemas';

import React, { useEffect, useState } from 'react';

import { Briefcase, Calendar, ChevronRight, FileText } from '../../../../shared/assets/AppIcons';
import EntityDetailDialog from '@/shared/components/EntityDetailDialog';
import { PrimaryActionButton, RowActions, StatusPill } from '../../../../shared/components/patterns';
import { formatAccountingCurrency, formatAccountingDate, getPaymentStatusMeta } from '../../utils';
import { getAccountingStatusTone } from '../shared/accountingPage.config';
import AccountingAttachmentsPanel from '../shared/AccountingAttachmentsPanel.jsx';
import AccountingCommentsSection from '../shared/AccountingCommentsSection.jsx';
import { useLinkedEntityCards } from '../../../../shared/components/linked-entities/LinkedEntityCardHost';
import ClientCustomFieldsSection from '../../../clients/components/ClientCustomFieldsSection';
import {
  archiveAccountingCustomField,
  createAccountingCustomField,
  listAccountingCustomFields,
} from '../../services/accounting-custom-fields.service';

const INVOICE_CUSTOM_FIELDS_API = {
  list: listAccountingCustomFields,
  create: createAccountingCustomField,
  archive: archiveAccountingCustomField,
};

function initials(value = '') {
  const parts = String(value || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return '?';
  return parts
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();
}

function compactDate(value) {
  if (!value) return '-';
  return new Date(value).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' });
}

function paymentTypeLabel(payment, linkedPayments) {
  if (payment?.recurring === true || payment?.type === 'ricorrente') return 'Ricorrente';
  if ((linkedPayments || []).length > 1 || payment?.type === 'rateizzato') return 'Rateizzato';
  return 'Unico';
}

export default function IncomeDetailDialog({
  open,
  onClose,
  mode = 'payment',
  payment,
  invoice,
  linkedPayments = [],
  planPayments,
  clients,
  onRegisterPayment,
  onEditPayment,
  onUpdatePayment,
  onRestorePayment,
  onUpdateInvoice,
  focusComments = false,
  initialMode = 'view',
  actionError = '',
}) {
  const { openEntityCard } = useLinkedEntityCards();
  const [editing, setEditing] = useState(false);
  const selectedPayment =
    payment || linkedPayments.find((item) => item.status !== 'incassato') || linkedPayments[0] || null;
  const source = mode === 'invoice' ? invoice : selectedPayment;
  const payments = linkedPayments.length ? linkedPayments : selectedPayment ? [selectedPayment] : [];
  const invoiceNumber =
    (mode === 'payment' ? selectedPayment?.invoiceNumber : invoice?.invoiceNumber) ||
    invoice?.invoiceNumber || selectedPayment?.invoiceNumber || source?.invoiceNumber || 'Fattura';
  const clientName = source?.clientResolvedName || source?.client || invoice?.client || 'Cliente non risolto';
  const selectedId = selectedPayment?.id == null ? null : String(selectedPayment.id);
  const activeIndex = Math.max(
    0,
    payments.findIndex((item) => String(item.id) === selectedId)
  );
  const activePayment = mode === 'payment' ? selectedPayment : payments[activeIndex] || selectedPayment;
  useEffect(() => {
    if (open) setEditing(initialMode === 'edit');
  }, [open, initialMode, source?.id]);
  if (!source) return null;
  const status = getPaymentStatusMeta(activePayment?.status || source.status);
  const amount = activePayment?.amount ?? source.totalAmount ?? source.amount;
  const canRegister = activePayment && !['incassato', 'stornato', 'annullato'].includes(activePayment.status);
  const title =
    mode === 'payment'
      ? `${invoiceNumber}${activePayment?.installment ? ` · rata ${activePayment.installment}` : ''}`
      : `${invoiceNumber}${activePayment?.installment ? ` · prossima rata ${activePayment.installment}` : ''}`;

  if (editing) return <EditIncomeDialog key={`${mode}:${source.id}`} open={open} mode={mode}
    source={mode === 'invoice' ? invoice : activePayment} payments={payments} onClose={() => setEditing(false)}
    onSave={mode === 'invoice' ? onUpdateInvoice && (value => onUpdateInvoice(invoice.id, value))
      : onUpdatePayment && (value => onUpdatePayment(activePayment.id, { ...value, paymentDate: value.status === 'incassato' ? value.paymentDate : null }))} />;

  if (mode === 'payment') return <PaymentDetailCard key={activePayment.id} open={open} onClose={onClose}
    payment={activePayment} invoice={invoice} allPayments={planPayments} clients={clients} title={title}
    onRegisterPayment={onRegisterPayment} onRestorePayment={onRestorePayment}
    onEdit={() => { setEditing(true); onEditPayment?.(activePayment); }}
    focusComments={focusComments} actionError={actionError} />;

  return (
    <EntityDetailDialog
      isOpen={open}
      onClose={onClose}
      className="accounting-detail accounting-detail--income"
      avatarLabel={initials(invoiceNumber)}
      title={title}
      subtitle={clientName}
      badges={[{ label: paymentTypeLabel(activePayment, payments), tone: 'active' }]}
      headerMeta={
        <>
          <div className="accounting-detail__amount">{formatAccountingCurrency(amount)}</div>
          <StatusPill
            label={status.label}
            tone={getAccountingStatusTone(activePayment?.status || source.status)}
          />
        </>
      }
      primaryActions={
        canRegister ? (
          <PrimaryActionButton iconKey="actions.accept" tone="success" onClick={() => onRegisterPayment?.(activePayment)}>
            Registra incasso
          </PrimaryActionButton>
        ) : activePayment?.status === 'stornato' && onRestorePayment ? (
          <PrimaryActionButton iconKey="actions.reset" onClick={() => onRestorePayment(activePayment)}>Ripristina incasso</PrimaryActionButton>
        ) : null
      }
      editActions={
        (mode === 'invoice' ? invoice : activePayment) ? (
          <RowActions
            actions={[
              {
                id: 'edit',
                iconKey: 'actions.edit',
                label: mode === 'invoice' ? 'Modifica fattura' : 'Modifica incasso',
                onClick: () => {
                  setEditing(true);
                  onEditPayment?.(activePayment);
                },
              },
            ]}
          />
        ) : null
      }
    >
      {actionError ? <p role="alert" className="wt-inline-error">{actionError}</p> : null}
      <div className="accounting-detail__grid">
        <section className="accounting-detail__section">
          <h3>Descrizione</h3>
          <p className="whitespace-pre-wrap">{activePayment?.notes || invoice?.description || source.description || 'Nessuna descrizione'}</p>
          {activePayment?.reason && <dl className="accounting-detail__facts"><div><dt>Causale</dt><dd>{activePayment.reason}</dd></div></dl>}
        </section>

        <section className="accounting-detail__section accounting-detail__section--right accounting-detail__section--span">
          <h3>Piano rate · fattura "{invoiceNumber}"</h3>
          <div className="accounting-detail__installments">
            {payments.length ? (
              payments.map((item, index) => {
                const itemStatus = getPaymentStatusMeta(item.status);
                const isActive = selectedId ? String(item.id) === selectedId : index === activeIndex;
                return (
                  <div
                    key={item.id || index}
                    className={`accounting-detail__installment ${isActive ? 'is-active' : ''}`.trim()}
                  >
                    <span>
                      Rata {item.installment || index + 1}/{payments.length} · {compactDate(item.dueDate)}
                    </span>
                    <span>{itemStatus.label}</span>
                  </div>
                );
              })
            ) : (
              <div className="accounting-detail__installment is-active">
                <span>Incasso unico · {compactDate(source.dueDate)}</span>
                <span>{status.label}</span>
              </div>
            )}
          </div>
        </section>

        <section className="accounting-detail__section">
          <h3>Date</h3>
          <dl className="accounting-detail__facts">
            <div>
              <dt>
                <FileText size={16} /> Data emissione
              </dt>
              <dd>{formatAccountingDate(source.issueDate || invoice?.issueDate)}</dd>
            </div>
            <div>
              <dt>
                <Calendar size={16} /> Scadenza
              </dt>
              <dd>{formatAccountingDate(activePayment?.dueDate || source.dueDate)}</dd>
            </div>
          </dl>
        </section>

        <section className="accounting-detail__section">
          <h3>Cliente</h3>
          <button
            type="button"
            disabled={!source.clientId}
            className="accounting-detail__entity-row"
            onClick={() => openEntityCard({ entityType: 'client', entityId: source.clientId })}
          >
            <span className="accounting-detail__avatar">{initials(clientName)}</span>
            <strong>{clientName}</strong>
            <ChevronRight size={16} />
          </button>
        </section>

        <section className="accounting-detail__section accounting-detail__section--right">
          <h3>Allegati</h3>
          {activePayment ? (
            <div className="accounting-detail__attachment">
              <FileText size={16} />
              <strong>Documenti incasso</strong>
              <AccountingAttachmentsPanel
                entity={activePayment}
                linkedEntityType="ACCOUNTING_PAYMENT"
                entityLabel={activePayment.invoiceNumber || `incasso ${activePayment.id}`}
                clientName={clientName}
              />
            </div>
          ) : (
            <p>Nessun incasso selezionato.</p>
          )}
        </section>

        <section className="accounting-detail__section">
          <h3>Affare collegato</h3>
          <button
            type="button"
            disabled={!(source.completedDealId || invoice?.completedDealId || source.inProgressDealId)}
            className="accounting-detail__entity-row"
            onClick={() =>
              openEntityCard({
                entityType: source.completedDealId || invoice?.completedDealId ? 'completedDeal' : 'inProgressDeal',
                entityId: source.completedDealId || invoice?.completedDealId || source.inProgressDealId,
              })
            }
          >
            <Briefcase size={16} />
            <strong>{source.assignment || invoice?.assignment || 'Nessun affare collegato'}</strong>
            <ChevronRight size={16} />
          </button>
        </section>

        {mode === 'payment' && activePayment?.invoiceId ? (
          <section className="accounting-detail__section">
            <h3>Fattura</h3>
            <button
              type="button"
              className="accounting-detail__entity-row"
              onClick={() => openEntityCard({ entityType: 'invoice', entityId: activePayment.invoiceId })}
            >
              <FileText size={16} />
              <strong>{invoiceNumber}</strong>
              <ChevronRight size={16} />
            </button>
          </section>
        ) : null}
        {mode === 'invoice' ? (
          <section className="accounting-detail__section accounting-detail__section--right">
            <h3>Incassi associati</h3>
            {payments.map((item) => (
              <button
                type="button"
                key={item.id}
                className="accounting-detail__entity-row"
                onClick={() => openEntityCard({ entityType: 'payment', entityId: item.id })}
              >
                <strong>Incasso #{item.id}</strong>
                <ChevronRight size={16} />
              </button>
            ))}
          </section>
        ) : null}

        <section className="accounting-detail__section accounting-detail__section--right">
          <h3>Descrizione interna</h3>
          <p>{activePayment?.notes || invoice?.notes || source.notes || 'Nessuna descrizione interna'}</p>
        </section>
      </div>
      {mode === 'invoice' ? (
        <ClientCustomFieldsSection
          readOnly
          formKey={invoiceFormSchema.id}
          values={invoice?.customFields || {}}
          api={INVOICE_CUSTOM_FIELDS_API}
        />
      ) : null}
      <EntityPartyLinks entity={source}  />
      <AccountingCommentsSection
        entityType={mode === 'invoice' ? 'invoice' : 'payment'}
        entityId={mode === 'invoice' ? invoice?.id : activePayment?.id}
        autoFocus={focusComments}
      />
    </EntityDetailDialog>
  );
}
